/**
 * Error_Handler.ts
 *
 * Single source of truth for extracting and displaying backend error messages.
 *
 * Backend error shapes handled:
 *   Platform API  → { message: string }  |  { title: string }  |  { errors: [{ message }] }
 *   Suki API      → { message: string }  |  { error: string }
 *   Network error → Error.message
 */

import { showToast } from '../components/toastComponent';

const MAX_LENGTH = 300;
const DEFAULT_FALLBACK = 'Something went wrong. Please try again.';

/**
 * SKPLATFORM-UICC-001 CH-U4 — what the person sees when the console forgets an
 * idempotency key. Deliberately says nothing about idempotency: this is a
 * client bug, not a user problem, and the backend's wording
 * ("X-Idempotency-Key is required for this endpoint") is both meaningless to
 * them and a leak of our internals into a toast.
 */
const IDEMPOTENCY_FALLBACK = 'Something went wrong. Please retry.';

/**
 * True when this is skplatform-api's `400 IDEMPOTENCY_KEY_REQUIRED`
 * (SKPLATFORM-CC-002 CH-5.1).
 *
 * Should never fire once CH-U4 has landed — the shared axios client stamps the
 * key on every required route. If it DOES fire, a call escaped the client layer
 * or our route table has drifted from the API's, and the console error below is
 * the only thing that will say so.
 */
export function isIdempotencyKeyRequired(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const res = (error as any).response;
  if (!res || res.status !== 400) return false;

  const data = res.data;
  if (!data || typeof data !== 'object') return false;

  if (data.code === 'IDEMPOTENCY_KEY_REQUIRED') return true;
  return (
    Array.isArray(data.errors) &&
    data.errors.some((e: any) => e?.code === 'IDEMPOTENCY_KEY_REQUIRED')
  );
}

/* Characters that are safe to display in a toast — blocks HTML/script injection */
const BLOCKED = /<|>|javascript:|data:/i;

function isSafeString(val: unknown): val is string {
  return typeof val === 'string' && val.trim().length > 0 && !BLOCKED.test(val);
}

/**
 * Extracts the most specific error message from an axios error object.
 * Always returns the real backend message when available.
 * Falls back to `fallback` only when no usable message is found.
 */
export function getApiErrorMessage(error: unknown, fallback = DEFAULT_FALLBACK): string {
  if (!error || typeof error !== 'object') return fallback;

  const err = error as any;
  const data = err?.response?.data;

  // CH-U4: checked BEFORE the message-extraction ladder below, which would
  // otherwise surface the backend's internal wording verbatim.
  if (isIdempotencyKeyRequired(error)) {
    // eslint-disable-next-line no-console
    console.error(
      '[idempotency] a required route was called without X-Idempotency-Key — ' +
        'the call bypassed the shared axios client, or IDEMPOTENT_REQUIRED_ROUTES ' +
        'has drifted from skplatform-api. URL:',
      err?.config?.url,
    );
    return IDEMPOTENCY_FALLBACK;
  }

  /* ── Priority order: pick the most specific message ── */

  // 1. data.message  (most APIs)
  if (isSafeString(data?.message)) return data.message.trim().slice(0, MAX_LENGTH);

  // 2. data.error.message  (sk-catalog-api envelope: { error: { code, message } })
  if (isSafeString(data?.error?.message)) return data.error.message.trim().slice(0, MAX_LENGTH);

  // 3. data.errors[0].message  (validation error arrays)
  const firstError = Array.isArray(data?.errors) ? data.errors[0] : null;
  if (isSafeString(firstError?.message)) return firstError.message.trim().slice(0, MAX_LENGTH);

  // 4. data.title  (Platform API RFC 7807 shape)
  if (isSafeString(data?.title)) return data.title.trim().slice(0, MAX_LENGTH);

  // 5. data.error  (Suki API short error string)
  if (isSafeString(data?.error)) return data.error.trim().slice(0, MAX_LENGTH);

  // 5. Network / JS error message
  if (isSafeString(err?.message)) return err.message.trim().slice(0, MAX_LENGTH);

  return fallback;
}

/**
 * Shows a backend error message in an error toast.
 * Use this in every catch block instead of manually extracting error.response.data.message.
 *
 * @param error   - The caught error (any shape)
 * @param title   - Toast heading (e.g. 'Login Failed', 'Save failed')
 * @param fallback - Optional custom fallback when no backend message is found
 */
export function handleApiError(error: unknown, title: string, fallback = DEFAULT_FALLBACK): void {
  const message = getApiErrorMessage(error, fallback);
  showToast('error', title, message);
}
